import React, { useEffect, useState } from "react";
import { api } from "../../api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const MessagesInbox = () => {
  const [messages, setMessages] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const list = await api.listInbox();
      setMessages(list);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => { load(); }, []);

  const onSelect = async (m) => {
    setSelected(m);
    if (!m.read) {
      try {
        await api.markMessageRead(m.id);
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      } catch (err) {
        console.error("Failed to mark message as read:", err);
      }
    }
  };

  const onDelete = async (m) => {
    if (!confirm(`Delete message from ${m.name}?`)) return;
    try {
      await api.deleteMessage(m.id);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      if (selected?.id === m.id) setSelected(null);
    } catch (e) {
      alert("Delete failed");
    }
  };

  if (messages === null) return <p className="p-8 font-serif text-[color:var(--ink-mute)]">Loading…</p>;

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="grid grid-cols-12 min-h-[70vh]">
      <aside className="col-span-12 md:col-span-5 border-r border-[color:var(--line)] p-5">
        <div className="flex items-baseline justify-between mb-4">
          <p className="font-serif text-2xl">Messages</p>
          <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">
            {messages.length} total{unread > 0 && ` · ${unread} unread`}
          </span>
        </div>

        {messages.length === 0 && (
          <p className="font-serif text-[13px] text-[color:var(--ink-mute)] italic">
            No messages yet — submissions from the Contact form will appear here.
          </p>
        )}

        <ul className="space-y-1" data-testid="messages-list">
          {messages.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => onSelect(m)}
                className={`w-full text-left p-3 border ${
                  selected?.id === m.id
                    ? "border-[color:var(--ink)] bg-[color:var(--paper-2)]"
                    : "border-transparent hover:bg-[color:var(--paper-2)]"
                }`}
                data-testid={`message-item-${m.id}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className={`font-serif text-[14px] leading-tight ${!m.read ? "font-semibold" : ""}`}>
                    {!m.read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--ink)] mr-2 align-middle" />}
                    {m.name}
                  </p>
                  <span className="font-serif text-[11px] text-[color:var(--ink-mute)] whitespace-nowrap">
                    {formatDate(m.created_at).split(",")[0]}
                  </span>
                </div>
                <p className="font-serif text-[12px] text-[color:var(--ink-mute)] mt-1 line-clamp-1">
                  {m.subject} — {m.message.slice(0, 60)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="col-span-12 md:col-span-7 p-6 md:p-8" data-testid="message-detail">
        {!selected ? (
          <div className="text-center text-[color:var(--ink-mute)] font-serif py-20">
            Select a message from the list to read it.
          </div>
        ) : (
          <div className="max-w-2xl">
            <p className="font-serif italic text-[color:var(--ink-mute)] text-sm">{selected.subject}</p>
            <h2 className="font-serif text-3xl mt-2">{selected.name}</h2>
            <p className="font-serif text-[color:var(--ink-mute)] mt-1">
              <a href={`mailto:${selected.email}`} className="tlink">{selected.email}</a>
              <span className="mx-2">·</span>
              {formatDate(selected.created_at)}
            </p>

            <div className="mt-8 pt-6 border-t border-[color:var(--line)]">
              <p className="font-serif text-lg leading-[1.55] whitespace-pre-wrap">{selected.message}</p>
            </div>

            <div className="mt-10 flex items-center gap-4">
              <a
                href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject)}`}
                className="btn-sober"
              >
                Reply by email
              </a>
              <button
                onClick={() => onDelete(selected)}
                className="font-serif text-[13px] text-red-600 tlink"
                data-testid="message-delete-button"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MessagesInbox;
